/**
 * FRIDGE-1175 — the in-body product modules (the "Shop this collection" bands,
 * `<!--CB:N-->`, and the single entity cards, `<!--EPC:N-->`) agree with the
 * section they sit in and with the article's own advice.
 *
 * Measured on production 10/18/2026, the ten Reddit-query articles: the band
 * under "1) The Default Answer: A Simple Whirlpool" held GE, Bosch and
 * Frigidaire models; the band under "3) The Step-Up Pick: Bosch 800 Series"
 * held an LG model whose door carries the ice, in an article that says to skip
 * through-door ice; the Midea section held a band of other makers' office
 * fridges; a band labelled "GE Wine Fridges" held no GE model. Four rules:
 *
 * 1. A section presents the makers its heading names and the makers of the
 *    author's own product cards (`<!--IPC:N-->`) inside it. A module in that
 *    section shows only those makers' products, or it is left out.
 * 2. A module labelled with a maker's collection (`ge-wine-fridges`) shows only
 *    that maker's products, the way the collection page filters its brand.
 * 3. When the article tells shoppers to skip through-door ice and water, no
 *    module shows a product whose own record names a door dispenser.
 * 4. In a maker's section a band never repeats the author's own card.
 *
 * Nothing is invented: a module that cannot be filled under these rules is
 * simply not rendered, and its marker index is kept for the sections after it.
 * A band left out still claims the collection it would have shown, so the
 * sections after it keep the collections they had. Product records are read
 * as stored: a door dispenser the record does not name is not inferred.
 * Pure: no Supabase, no React.
 */
import { BRAND_RULES } from '../brand-rules'
import { parseCollectionUrl } from '../collection-url-parser'

/** Trailing corporate words a maker's catalog name carries ("GE Appliances", "Avanti Products"). */
const CORPORATE_SUFFIX = /\s+(?:appliances?|products?|usa|inc|llc|co|company|corp|corporation|america)$/

/** Spellings of one maker that normalize to different strings. */
const MAKER_ALIASES: Record<string, string> = {
  'jenn air': 'jennair',
  'kitchen aid': 'kitchenaid',
  'ge cafe': 'cafe',
  'lg electronics': 'lg',
  'samsung electronics': 'samsung',
}

/**
 * Makers a heading can name. The appliance makers our articles discuss by name;
 * a heading is read against this list plus the makers of the article's own
 * product cards. Ordinary words that are also catalog brand names (True, Sharp,
 * Element, Marvel) are left out on purpose: a heading that says "The True Cost"
 * names no maker. Café is a maker unless the heading means a place ("your
 * café", measured 10/18/2026: 4 of the 5 corpus headings naming Café mean GE's
 * brand, 1 means a coffee shop).
 */
export const NAMED_MAKERS: readonly string[] = [
  'Whirlpool', 'Maytag', 'Amana', 'KitchenAid', 'JennAir', 'GE', 'GE Profile', 'Monogram', 'Hotpoint', 'Haier',
  'Frigidaire', 'Electrolux', 'Samsung', 'LG', 'Bosch', 'Thermador', 'Gaggenau', 'Miele', 'Sub-Zero', 'Liebherr',
  'Fisher & Paykel', 'Viking', 'Thor Kitchen', 'ZLINE', 'Midea', 'Hisense', 'Danby', 'Galanz', 'Insignia', 'Kenmore',
  'Beko', 'Blomberg', 'Smeg', 'Summit', 'Avanti', 'Magic Chef', 'Whynter', 'NewAir', 'EdgeStar', 'Kegco', 'Kalamera',
  'Allavino', 'Vinotemp', 'Koolatron', 'Dometic', 'U-Line', 'Perlick', 'Dacor', 'Hestan', 'Bertazzoni', 'Zephyr',
  'Euhomy', 'Igloo', 'Black+Decker', 'Arctic King', 'Commercial Cool', 'Café',
]

/** A maker word read as a place in this context names no maker ("for your café"). */
const PLACE_CONTEXT: Record<string, RegExp> = {
  cafe: /(?<= )(?:your|a|the|my|our|small|local|coffee|every) cafe(?= )/g,
}

/** Maker keys never read out of a heading: ordinary words that are also catalog brand names. */
const HEADING_STOP_KEYS = new Set([
  'true', 'generic', 'element', 'sharp', 'mist', 'capital', 'profile', 'eternal', 'artisan', 'azure', 'blaze', 'bull',
  'hinge', 'maine', 'spire', 'corona', 'icebox', 'bodega', 'lynx', 'equator', 'marvel', 'premium', 'simple', 'basic',
  'value', 'prime', 'pro', 'max', 'smart', 'home', 'ice', 'cool', 'chill', 'frost', 'arctic', 'polar', 'garage',
  'wine', 'beer', 'mini', 'compact', 'select', 'unbranded',
])

function plainText(value: string): string {
  return String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:amp|#0*38);/gi, ' ')
    .replace(/&[a-z]+;|&#x?[0-9a-f]+;/gi, ' ')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** One comparable key per maker: "GE Appliances" → "ge", "Café" → "cafe", "Sub-Zero" → "sub zero". */
export function makerKey(name: string | null | undefined): string {
  let key = plainText(name ?? '')
  for (let prev = ''; prev !== key; ) {
    prev = key
    key = key.replace(CORPORATE_SUFFIX, '')
  }
  return MAKER_ALIASES[key] ?? key
}

/** True when the product's maker is one of the keys ("GE Profile" belongs to "ge"). */
export function productFromMakers(product: { brand?: string | null }, makers: readonly string[]): boolean {
  const key = makerKey(product.brand)
  if (!key) return false
  return makers.some(maker => key === maker || key.startsWith(`${maker} `))
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** The makers a heading names, longest name first so "GE Profile" is not also read as "GE". */
export function headingMakers(headingHtml: string, extraMakers: ReadonlyArray<string | null | undefined> = []): string[] {
  const keys = new Set<string>()
  for (const name of [...NAMED_MAKERS, ...extraMakers]) {
    const key = makerKey(name)
    if (key.length >= 2 && !HEADING_STOP_KEYS.has(key)) keys.add(key)
  }
  let remaining = ` ${plainText(headingHtml)} `
  for (const pattern of Object.values(PLACE_CONTEXT)) remaining = remaining.replace(pattern, ' ')
  const found: string[] = []
  for (const key of [...keys].sort((a, b) => b.length - a.length)) {
    const pattern = new RegExp(`(?<= )${escapeRegExp(key)}(?= )`, 'g')
    if (!pattern.test(remaining)) continue
    found.push(key)
    remaining = remaining.replace(pattern, ' ')
  }
  return found
}

/**
 * For each `<!--CB:N-->` or `<!--EPC:N-->` marker: the makers its section
 * presents (rule 1). An empty list means the section names no maker and holds no
 * author card, and the module keeps its usual choice.
 */
export function articleSectionMakers(
  html: string,
  marker: 'CB' | 'EPC',
  authorBrands: ReadonlyArray<string | null | undefined> = [],
): Map<number, string[]> {
  const sections: Array<{ makers: Set<string>; slots: number[] }> = [{ makers: new Set(), slots: [] }]
  const tokens = new RegExp(`<h2\\b[^>]*>([\\s\\S]*?)<\\/h2>|<!--IPC:(\\d+)-->|<!--${marker}:(\\d+)-->`, 'gi')
  for (const token of String(html ?? '').matchAll(tokens)) {
    const section = sections[sections.length - 1]
    if (token[1] !== undefined) {
      sections.push({ makers: new Set(headingMakers(token[1], authorBrands)), slots: [] })
    } else if (token[2] !== undefined) {
      const key = makerKey(authorBrands[Number(token[2])])
      if (key) section.makers.add(key)
    } else {
      section.slots.push(Number(token[3]))
    }
  }
  const out = new Map<number, string[]>()
  for (const section of sections) for (const slot of section.slots) out.set(slot, [...section.makers])
  return out
}

/**
 * Rule 2: the maker a collection slug filters to ("ge-wine-fridges" → "ge"), or
 * null. The parser reads any leading word as a brand ("compact-refrigerators"
 * parses as brand "compact"), so only a name in the site's brand registry
 * (lib/brand-rules.ts) or the named makers counts.
 */
export function collectionMaker(slug: string): string | null {
  const parsed = parseCollectionUrl(slug)
  if (!parsed.brandSlug) return null
  const registryKey = parsed.brandSlug.replace(/-/g, ' ').toLowerCase()
  const key = makerKey(parsed.brandName || parsed.brandSlug)
  const known = BRAND_RULES[registryKey] != null || NAMED_MAKERS.some(name => makerKey(name) === key)
  return known && key ? key : null
}

/**
 * Rule 3, the article side: the article tells shoppers to skip through-door ice
 * and water. Read as advice only ("skip the through-door ice", "skip in-door ice
 * and water", "no ice in the door"), never a description such as "avoid opening
 * the door while ice forms" or "some models forgo through-the-door dispensers".
 * An article whose title is about a dispenser is never read this way.
 */
export function articleAdvisesAgainstDoorDispensers(text: string, title?: string | null): boolean {
  if (/dispens/i.test(String(title ?? ''))) return false
  const body = String(text ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ')
  return (
    /\b(?:skip|skipping|avoid|avoiding)\s+(?:the\s+|a\s+|any\s+)?(?:through[-\s](?:the[-\s])?door|in[-\s]door|door)\s+(?:ice|water|dispensers?)\b/i.test(body) ||
    /\b(?:no\s+ice\s+in\s+the\s+door|nothing\s+through\s+the\s+door|keep(?:ing)?\s+(?:the\s+)?ice\s+out\s+of\s+the\s+door)\b/i.test(body)
  )
}

/**
 * Rule 3, the product side: the product's own title or feature list names a
 * door dispenser or a door ice maker. "Internal ice and water dispenser" is the
 * opposite and never counts. Unrecorded means not excluded: only what the
 * record says is read.
 */
export function productHasDoorDispenser(product: { title?: string | null; features?: readonly string[] | null }): boolean {
  const text = [product.title ?? '', ...(product.features ?? [])]
    .join(' | ')
    .replace(/\b(?:internal|interior|inside|in[-\s]fridge|in[-\s]cabinet)\s+(?:(?:ice|water)\s*(?:&|and|\+|\/)\s*)?(?:ice|water)\s+dispens\w*/gi, ' ')
  return (
    /\b(?:ice\s*(?:&|and|\+|\/)\s*water|water\s*(?:&|and|\+|\/)\s*ice)\s+dispens/i.test(text) ||
    /\b(?:external|exterior|through[-\s]the[-\s]door|thru[-\s]the[-\s]door|door)\s+(?:(?:ice|water)\s*(?:&|and|\+|\/)\s*)?(?:ice|water)\s+dispens/i.test(text) ||
    /\bin[-\s]door\s+ice\b/i.test(text) ||
    /\bspace\s*plus\b/i.test(text) ||
    /\bdispenser\s+(?:in|on)\s+the\s+door\b/i.test(text)
  )
}

/** What the page knows about the article that the module rules read. */
export interface ArticleModuleFit {
  /** The author's own product cards in `<!--IPC:N-->` order (brand and link). */
  authorProducts?: ReadonlyArray<{ brand?: string | null; href?: string | null }>
  /** articleAdvisesAgainstDoorDispensers for this article. */
  noDoorDispensers?: boolean
}
